import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Edit2, History, Plus, Trash2, UsersRound } from "lucide-react";
import { DashboardLayout } from "../components/layout/DashboardLayout";
import { Header } from "../components/layout/Header";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { useAuth } from "../contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL || "http://localhost:8000"}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:8000";

const initialForm = {
  merchant_id: "",
  name: "",
  phone: "",
  bank_name: "",
  bank_account_name: "",
  bank_account_number: "",
  ktp_photo_path: "",
  sort_order: "",
  is_active: true,
};

const modalFieldClass =
  "rounded-sm border-transparent bg-white/[0.04] text-white shadow-none ring-1 ring-white/[0.035] transition-colors focus-visible:ring-white/15";
const modalDialogClass =
  "border-transparent bg-zinc-900 text-white shadow-2xl ring-1 ring-white/[0.04]";
const modalOutlineButtonClass =
  "flex-1 rounded-sm border-transparent bg-white/[0.03] text-white ring-1 ring-white/[0.04] hover:bg-white/[0.06]";
const defaultBankOptions = ["BCA", "BRI", "BNI", "MANDIRI", "SEABANK"];
const monthOptions = [
  { value: "01", label: "Januari" },
  { value: "02", label: "Februari" },
  { value: "03", label: "Maret" },
  { value: "04", label: "April" },
  { value: "05", label: "Mei" },
  { value: "06", label: "Juni" },
  { value: "07", label: "Juli" },
  { value: "08", label: "Agustus" },
  { value: "09", label: "September" },
  { value: "10", label: "Oktober" },
  { value: "11", label: "November" },
  { value: "12", label: "Desember" },
];

const normalizeText = (value) => String(value || "").trim().toLowerCase();
const normalizeCoachText = (value) => String(value || "").trim().toLowerCase().replace(/\s+/g, " ");
const resolveFileUrl = (path) => {
  const value = String(path || "").trim();
  if (!value) return "";
  if (value.startsWith("http://") || value.startsWith("https://")) return value;
  return `${BACKEND_URL}${value}`;
};
const isCoachNameMatch = (selectedCoachName, candidateCoachName) => {
  const selected = normalizeCoachText(selectedCoachName);
  const candidate = normalizeCoachText(candidateCoachName);
  if (!selected || !candidate) return false;
  return selected === candidate || selected.includes(candidate) || candidate.includes(selected);
};

const formatDateTime = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("id-ID", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatDateOnly = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("id-ID", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const extractDateOnly = (value) => {
  const raw = String(value || "").trim();
  const matched = raw.match(/^(\d{4}-\d{2}-\d{2})/);
  if (matched?.[1]) return matched[1];
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return "";
  const year = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, "0");
  const day = String(parsed.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const toMonthNumberValue = (value) => {
  const dateValue = extractDateOnly(value);
  if (!dateValue) return "";
  return String(dateValue).slice(5, 7);
};

const extractDateFromCardioDescription = (descriptionText) => {
  const description = String(descriptionText || "");
  const matched = description.match(/tanggal\s+(\d{4}-\d{2}-\d{2})/i);
  return matched?.[1] || "";
};

const todayDateValue = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const isDateBeforeToday = (value, todayValue = todayDateValue()) => {
  const dateValue = extractDateOnly(value);
  if (!dateValue) return false;
  return dateValue < todayValue;
};

const getClassStatusLabel = (status) => {
  if (status === "done") return "Done";
  if (status === "cancelled") return "Cancel - Coach Izin";
  return "Not Done";
};

const getClassStatusTextClass = (status) => {
  if (status === "done") return "font-semibold text-emerald-300";
  if (status === "cancelled") return "font-semibold text-amber-300";
  return "font-semibold text-rose-300";
};

const isManualClassSlot = (item) => {
  const sourceType = normalizeText(item?.source_type);
  const source = normalizeText(item?.source);
  const fullName = normalizeText(item?.full_name);
  if (sourceType !== "booking") return false;
  if (source !== "admin_manual_coach") return false;
  return fullName === "kelas umum";
};

export default function CardioCoaches() {
  const { user } = useAuth();
  const isAdmin = String(user?.role || "").trim().toLowerCase() === "admin";
  const [coaches, setCoaches] = useState([]);
  const [merchants, setMerchants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCoachMembersOpen, setIsCoachMembersOpen] = useState(false);
  const [isCoachHistoryMode, setIsCoachHistoryMode] = useState(false);
  const [selectedCoach, setSelectedCoach] = useState(null);
  const [coachMembersLoading, setCoachMembersLoading] = useState(false);
  const [coachMembers, setCoachMembers] = useState([]);
  const [selectedClassKey, setSelectedClassKey] = useState("");
  const [manualClassForm, setManualClassForm] = useState({
    class_name: "",
    class_date: todayDateValue(),
  });
  const [manualClassOptions, setManualClassOptions] = useState([]);
  const [addingManualClass, setAddingManualClass] = useState(false);
  const [coachSummary, setCoachSummary] = useState({
    totalClasses: 0,
    totalMembers: 0,
    doneCount: 0,
    notDoneCount: 0,
    cancelledCount: 0,
  });
  const [editingCoach, setEditingCoach] = useState(null);
  const [formData, setFormData] = useState(initialForm);
  const [uploadingKtp, setUploadingKtp] = useState(false);
  const [coachMonthFilter, setCoachMonthFilter] = useState("");
  const [bankOptions, setBankOptions] = useState(defaultBankOptions);
  const [pendingStatusUpdate, setPendingStatusUpdate] = useState(null);

  const sortedCoaches = useMemo(
    () =>
      [...coaches].sort((a, b) => {
        const orderA = Number(a.sort_order || 0);
        const orderB = Number(b.sort_order || 0);
        if (orderA !== orderB) return orderA - orderB;
        return String(a.name || "").localeCompare(String(b.name || ""));
      }),
    [coaches]
  );

  const groupedCoachMembers = useMemo(() => {
    const groups = new Map();
    coachMembers.forEach((item) => {
      const normalizedClassName = String(item.schedule_name || "").trim() || "Kelas Umum";
      const classDate = extractDateOnly(item.class_date || item.created_at) || "undated";
      const key = `${normalizeText(normalizedClassName)}__${classDate}`;
      const existing = groups.get(key) || {
        class_key: key,
        class_name: normalizedClassName,
        class_date: classDate,
        sessions: [],
      };
      existing.sessions.push(item);
      groups.set(key, existing);
    });

    return [...groups.values()]
      .map((group) => {
        const sortedSessions = [...group.sessions].sort((a, b) =>
          String(b.created_at).localeCompare(String(a.created_at))
        );
        const realMemberSessions = sortedSessions.filter((session) => !isManualClassSlot(session));
        const hasMemberSessions = realMemberSessions.length > 0;
        const manualSlot = sortedSessions.find((session) => isManualClassSlot(session));
        const manualStatus = normalizeText(manualSlot?.attendance_status);
        const classStatus = ["done", "not_done", "cancelled"].includes(manualStatus) ? manualStatus : null;
        return {
          ...group,
          sessions: sortedSessions,
          displaySessions: realMemberSessions,
          totalSessions: realMemberSessions.length,
          classStatus,
          isEmptyClass: !hasMemberSessions,
          classDoneCount: classStatus === "done" ? 1 : 0,
          classNotDoneCount: classStatus === "not_done" ? 1 : 0,
          classCancelledCount: classStatus === "cancelled" ? 1 : 0,
          latestSession: sortedSessions[0] || null,
        };
      })
      .sort((a, b) =>
        String(b.class_date || "").localeCompare(String(a.class_date || ""))
      );
  }, [coachMembers]);

  const selectedClassSessions = useMemo(() => {
    if (!selectedClassKey) return null;
    return groupedCoachMembers.find((item) => item.class_key === selectedClassKey) || null;
  }, [groupedCoachMembers, selectedClassKey]);

  const visibleCoachClassGroups = useMemo(() => {
    const month = String(coachMonthFilter || "");
    const todayValue = todayDateValue();
    const baseGroups = isCoachHistoryMode
      ? groupedCoachMembers.filter((item) => item.classStatus === "done")
      : groupedCoachMembers.filter((item) => !isDateBeforeToday(item.class_date, todayValue));
    if (!month) return baseGroups;
    return baseGroups.filter((item) => toMonthNumberValue(item.class_date) === month);
  }, [coachMonthFilter, groupedCoachMembers, isCoachHistoryMode]);

  const visibleCoachSummary = useMemo(() => {
    const totalMembers = new Set(
      visibleCoachClassGroups
        .flatMap((item) => item.displaySessions || [])
        .map((session) => session.member_key)
        .filter(Boolean)
    ).size;
    return {
      totalClasses: visibleCoachClassGroups.length,
      totalMembers,
      doneCount: visibleCoachClassGroups.reduce((sum, item) => sum + Number(item.classDoneCount || 0), 0),
      notDoneCount: visibleCoachClassGroups.reduce((sum, item) => sum + Number(item.classNotDoneCount || 0), 0),
      cancelledCount: visibleCoachClassGroups.reduce((sum, item) => sum + Number(item.classCancelledCount || 0), 0),
    };
  }, [visibleCoachClassGroups]);

  useEffect(() => {
    fetchCoaches();
    fetchBankOptions();
    fetchMerchants();
  }, []);

  const fetchMerchants = async () => {
    try {
      const { data } = await axios.get(`${API}/merchants`, { withCredentials: true });
      setMerchants(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Failed to fetch merchants:", error);
      setMerchants([]);
    }
  };

  const fetchBankOptions = async () => {
    try {
      const { data } = await axios.get(`${API}/banks`, { withCredentials: true });
      const activeBanks = (Array.isArray(data) ? data : [])
        .filter((item) => item && item.is_active !== false)
        .map((item) => String(item.name || "").trim())
        .filter(Boolean);
      setBankOptions(activeBanks.length > 0 ? activeBanks : defaultBankOptions);
    } catch (error) {
      console.error("Failed to fetch bank options:", error);
      setBankOptions(defaultBankOptions);
    }
  };

  useEffect(() => {
    if (!isCoachMembersOpen) return;
    if (!manualClassForm.class_name) return;
    if (manualClassOptions.includes(manualClassForm.class_name)) return;
    setManualClassForm((prev) => ({ ...prev, class_name: "" }));
  }, [isCoachMembersOpen, manualClassForm.class_name, manualClassOptions]);

  const fetchCoaches = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/cardio-coaches`, {
        withCredentials: true,
      });
      setCoaches(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Failed to fetch cardio coaches:", error);
      alert(error.response?.data?.detail || "Gagal mengambil daftar coach cardio.");
    } finally {
      setLoading(false);
    }
  };

  const fetchCoachMembersData = async (coach) => {
    if (!coach) return;
    setCoachMembersLoading(true);
    try {
      const [schedulesRes, bookingsRes, paymentsRes, plansRes] = await Promise.all([
        axios.get(`${API}/schedules`, { withCredentials: true }),
        axios.get(`${API}/class-bookings`, { withCredentials: true }),
        axios.get(`${API}/payments`, { withCredentials: true }),
        axios.get(`${API}/plans`, { withCredentials: true }),
      ]);
      const schedules = Array.isArray(schedulesRes.data) ? schedulesRes.data : [];
      const bookings = Array.isArray(bookingsRes.data) ? bookingsRes.data : [];
      const payments = Array.isArray(paymentsRes.data) ? paymentsRes.data : [];
      const plans = Array.isArray(plansRes.data) ? plansRes.data : [];
      const coachName = String(coach?.name || "").trim();
      const coachMerchantId = String(coach?.merchant_id || "").trim();

      const coachSchedules = schedules.filter(
        (item) => isCoachNameMatch(coachName, item?.instructor)
      );
      const cardioPlans = plans.filter((item) => {
        if (!item || item.is_active === false) return false;
        return String(item.category || "").trim().toUpperCase() === "CARDIO";
      });
      const merchantCardioPlans = coachMerchantId
        ? cardioPlans.filter((item) => String(item?.merchant_id || "").trim() === coachMerchantId)
        : cardioPlans;
      const coachSpecificCardioPlans = merchantCardioPlans.filter(
        (item) => isCoachNameMatch(coachName, item?.coach_name)
      );
      const effectiveCardioPlans = coachSpecificCardioPlans.length > 0 ? coachSpecificCardioPlans : merchantCardioPlans;
      const planClassOptions = [...new Set(
        effectiveCardioPlans
          .map((item) => String(item?.name || "").trim())
          .filter(Boolean)
      )].sort((a, b) => String(a).localeCompare(String(b)));
      const coachCardioPlanNameSet = new Set(
        coachSpecificCardioPlans
          .map((item) => normalizeText(item?.name))
          .filter(Boolean)
      );
      const scheduleIdSet = new Set(coachSchedules.map((item) => String(item.id || "")));
      const scheduleNameSet = new Set(
        coachSchedules.map((item) => normalizeText(item.name))
      );

      const relatedBookings = bookings
        .filter((item) => {
          const scheduleId = String(item.schedule_id || "");
          const scheduleName = normalizeText(item.schedule_name);
          const bookingCoachName = String(item.coach_name || "").trim();
          return scheduleIdSet.has(scheduleId) || scheduleNameSet.has(scheduleName) || isCoachNameMatch(coachName, bookingCoachName);
        })
        .map((item, index) => {
          const isManualSlot =
            normalizeText(item.source) === "admin_manual_coach" &&
            normalizeText(item.full_name) === "kelas umum";
          const statusSelected = item.attendance_status_selected === true;
          const rawAttendanceStatus = normalizeText(item.attendance_status);
          const attendanceStatus = statusSelected ? rawAttendanceStatus || "not_done" : "";
          const isDone = attendanceStatus === "done";
          return {
            id: item.id || `${index}-${item.full_name || ""}`,
            source_type: "booking",
            source: String(item.source || ""),
            full_name: item.full_name || "Tanpa Nama",
            schedule_name: item.schedule_name || "-",
            day_of_week: item.day_of_week || "-",
            start_time: item.start_time || "-",
            payment_status: item.payment_status || "Pending",
            status: item.status || "booked",
            attendance_status: attendanceStatus,
            attendance_status_selected: statusSelected,
            class_date: extractDateOnly(item.class_date || item.created_at),
            created_at: item.created_at || "",
            isDone,
            member_key: normalizeText(item.email) || normalizeText(item.phone) || normalizeText(item.full_name),
          };
        })
        .sort((a, b) => String(b.created_at).localeCompare(String(a.created_at)));

      const relatedCardioPayments = payments
        .filter((item) => {
          const description = String(item.description || "");
          if (!description) return false;
          const coachFromDescription = description.match(/coach\s+(.+?)(?:\s*-\s*tanggal\b|$)/i)?.[1] || "";
          if (coachFromDescription) {
            return isCoachNameMatch(coachName, coachFromDescription);
          }

          // Legacy cardio payments may not include "- Coach ...", but must still
          // reference a cardio plan explicitly assigned to this coach.
          const paymentClassName = normalizeText(item.class_name);
          return paymentClassName ? coachCardioPlanNameSet.has(paymentClassName) : false;
        })
        .map((item, index) => {
          const description = String(item.description || "").trim();
          const packageMatch = description.match(/^membership\s+(.+?)(?:\s*-\s*(?:kelas|coach|tanggal)\b.*)?$/i);
          const classNameFromPayment = String(item.class_name || "").trim();
          const className = classNameFromPayment || packageMatch?.[1]?.trim() || description;
          const classDateFromDescription = extractDateFromCardioDescription(description);
          const sessionStatus = normalizeText(item.session_status) || "not_done";
          const isDone = sessionStatus === "done";
          return {
            id: item.id || `payment-${index}-${item.member_name || ""}`,
            source_type: "payment",
            source: String(item.source || ""),
            full_name: item.member_name || "Tanpa Nama",
            schedule_name: className,
            day_of_week: "-",
            start_time: "-",
            payment_status: String(item.status || "Pending"),
            status: "payment",
            attendance_status: isDone ? "done" : "not_done",
            class_date: extractDateOnly(classDateFromDescription || item.class_date || item.created_at),
            created_at: item.created_at || "",
            isDone,
            member_key:
              normalizeText(item.member_id) ||
              normalizeText(item.member_name) ||
              normalizeText(item.transaction_number),
          };
        });

      const mergedRows = [...relatedBookings, ...relatedCardioPayments].sort((a, b) =>
        String(b.created_at).localeCompare(String(a.created_at))
      );
      const actualMemberRows = mergedRows.filter((item) => !isManualClassSlot(item));
      const groupedClassStatusMap = new Map();
      mergedRows.forEach((item) => {
        const className = normalizeText(item.schedule_name || "");
        const classDate = extractDateOnly(item.class_date || item.created_at);
        const key = `${className}__${classDate}`;
        const statusList = groupedClassStatusMap.get(key) || [];
        statusList.push(Boolean(item.isDone));
        groupedClassStatusMap.set(key, statusList);
      });

      const uniqueMemberCount = new Set(
        actualMemberRows.map((item) => item.member_key).filter(Boolean)
      ).size;
      const doneClassCount = [...groupedClassStatusMap.values()].filter(
        (statuses) => statuses.length > 0 && statuses.every(Boolean)
      ).length;
      const uniqueClassCount = new Set(
        mergedRows.map((item) => {
          const className = normalizeText(item.schedule_name || "");
          const classDate = extractDateOnly(item.class_date || item.created_at);
          return `${className}__${classDate}`;
        }).filter(Boolean)
      ).size;

      setCoachMembers(mergedRows);
      setManualClassOptions(planClassOptions);
      setCoachSummary({
        totalClasses: uniqueClassCount,
        totalMembers: uniqueMemberCount,
        doneCount: doneClassCount,
        notDoneCount: Math.max(uniqueClassCount - doneClassCount, 0),
        cancelledCount: 0,
      });
    } catch (error) {
      console.error("Failed to fetch coach class members:", error);
      alert(error.response?.data?.detail || "Gagal mengambil daftar member coach.");
      setCoachMembers([]);
      setManualClassOptions([]);
      setCoachSummary({
        totalClasses: 0,
        totalMembers: 0,
        doneCount: 0,
        notDoneCount: 0,
        cancelledCount: 0,
      });
    } finally {
      setCoachMembersLoading(false);
    }
  };

  const openCoachMembers = async (coach, options = {}) => {
    const historyMode = !!options.historyMode;
    setSelectedCoach(coach);
    setIsCoachHistoryMode(historyMode);
    setSelectedClassKey("");
    setCoachMonthFilter("");
    setManualClassForm((prev) => ({ ...prev, class_date: todayDateValue() }));
    setIsCoachMembersOpen(true);
    await fetchCoachMembersData(coach);
  };

  const updateClassDoneStatus = async (group, nextStatus) => {
    if (!selectedCoach || !group) return;
    const manualSlot = (Array.isArray(group.sessions) ? group.sessions : []).find((item) => isManualClassSlot(item));
    try {
      if (manualSlot?.id) {
        await axios.patch(
          `${API}/class-bookings/${manualSlot.id}/attendance`,
          { attendance_status: nextStatus },
          { withCredentials: true }
        );
      } else {
        await axios.post(
          `${API}/class-bookings/manual`,
          {
            coach_name: selectedCoach.name,
            class_name: group.class_name,
            class_date: extractDateOnly(group.class_date) || todayDateValue(),
            attendance_status: nextStatus,
          },
          { withCredentials: true }
        );
      }
      await fetchCoachMembersData(selectedCoach);
    } catch (error) {
      console.error("Failed to update class status:", error);
      alert(error.response?.data?.detail || "Gagal update status kelas.");
    }
  };

  const requestClassDoneStatus = (group, nextStatus) => {
    if (!group) return;
    setPendingStatusUpdate({ group, nextStatus });
  };

  const handleAddManualClass = async (event) => {
    event.preventDefault();
    if (!selectedCoach?.name) return;
    const selectedClassName = String(manualClassForm.class_name || "").trim();
    if (!selectedClassName) {
      alert("Nama kelas wajib dipilih.");
      return;
    }
    try {
      setAddingManualClass(true);
      await axios.post(
        `${API}/class-bookings/manual`,
        {
          coach_name: selectedCoach.name,
          class_name: selectedClassName,
          class_date: manualClassForm.class_date,
        },
        { withCredentials: true }
      );
      setManualClassForm({
        class_name: "",
        class_date: todayDateValue(),
      });
      await fetchCoachMembersData(selectedCoach);
    } catch (error) {
      console.error("Failed to add manual class:", error);
      alert(error.response?.data?.detail || "Gagal menambahkan kelas manual.");
    } finally {
      setAddingManualClass(false);
    }
  };

  const deleteClassSession = async (session) => {
    if (!isAdmin) {
      alert("Kasir hanya dapat melihat detail Master Coach.");
      return;
    }
    if (!session?.id) return;
    if (!window.confirm("Hapus kelas ini?")) return;
    try {
      if (session.source_type === "booking") {
        await axios.delete(`${API}/class-bookings/${session.id}`, { withCredentials: true });
      } else {
        await axios.delete(`${API}/payments/${session.id}`, { withCredentials: true });
      }
      await fetchCoachMembersData(selectedCoach);
    } catch (error) {
      console.error("Failed to delete class session:", error);
      alert(error.response?.data?.detail || "Gagal menghapus kelas.");
    }
  };

  const deleteGroupedSessions = async (group) => {
    if (!isAdmin) {
      alert("Kasir hanya dapat melihat detail Master Coach.");
      return;
    }
    const sessions = Array.isArray(group?.sessions) ? group.sessions : [];
    const deletable = sessions.filter(
      (item) => (item.source_type === "booking" || item.source_type === "payment") && item.id
    );
    if (!deletable.length) {
      alert("Tidak ada data kelas yang bisa dihapus pada baris ini.");
      return;
    }
    if (!window.confirm(`Hapus ${deletable.length} data kelas pada baris ini?`)) return;
    try {
      await Promise.all(
        deletable.map((session) => {
          if (session.source_type === "booking") {
            return axios.delete(`${API}/class-bookings/${session.id}`, { withCredentials: true });
          }
          return axios.delete(`${API}/payments/${session.id}`, { withCredentials: true });
        })
      );
      if (selectedClassKey === group.class_key) {
        setSelectedClassKey("");
      }
      await fetchCoachMembersData(selectedCoach);
    } catch (error) {
      console.error("Failed to delete grouped sessions:", error);
      alert(error.response?.data?.detail || "Gagal menghapus data kelas.");
    }
  };

  const openCreate = () => {
    if (!isAdmin) return;
    setEditingCoach(null);
    setFormData({ ...initialForm, merchant_id: merchants[0]?.id || "", is_active: true });
    setIsModalOpen(true);
  };

  const openEdit = (coach) => {
    if (!isAdmin) return;
    setEditingCoach(coach);
    setFormData({
      merchant_id: String(coach.merchant_id || ""),
      name: String(coach.name || ""),
      phone: String(coach.phone || ""),
      bank_name: String(coach.bank_name || ""),
      bank_account_name: String(coach.bank_account_name || ""),
      bank_account_number: String(coach.bank_account_number || ""),
      ktp_photo_path: String(coach.ktp_photo_path || ""),
      sort_order: String(coach.sort_order ?? ""),
      is_active: coach.is_active !== false,
    });
    setIsModalOpen(true);
  };

  const handleKtpUpload = async (event) => {
    if (!isAdmin) {
      alert("Kasir hanya dapat melihat detail Master Coach.");
      return;
    }
    const file = event.target.files?.[0];
    if (!file) return;

    const body = new FormData();
    body.append("file", file);

    try {
      setUploadingKtp(true);
      const { data } = await axios.post(`${API}/uploads/cardio-ktp-image`, body, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      setFormData((prev) => ({ ...prev, ktp_photo_path: data?.path || "" }));
    } catch (error) {
      console.error("Failed to upload KTP coach cardio:", error);
      alert(error.response?.data?.detail || "Gagal upload foto KTP.");
    } finally {
      setUploadingKtp(false);
      event.target.value = "";
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!isAdmin) {
      alert("Kasir hanya dapat melihat detail Master Coach.");
      return;
    }

    const payload = {
      merchant_id: String(formData.merchant_id || "").trim(),
      name: String(formData.name || "").trim(),
      phone: String(formData.phone || "").trim(),
      bank_name: String(formData.bank_name || "").trim(),
      bank_account_name: String(formData.bank_account_name || "").trim(),
      bank_account_number: String(formData.bank_account_number || "").trim(),
      ktp_photo_path: String(formData.ktp_photo_path || "").trim(),
      sort_order: Number(formData.sort_order || 0),
      is_active: !!formData.is_active,
    };

    if (!payload.name) {
      alert("Nama coach cardio wajib diisi.");
      return;
    }
    if (!payload.merchant_id) {
      alert("Merchant wajib dipilih.");
      return;
    }

    try {
      if (editingCoach) {
        await axios.put(`${API}/cardio-coaches/${editingCoach.id}`, payload, { withCredentials: true });
      } else {
        await axios.post(`${API}/cardio-coaches`, payload, { withCredentials: true });
      }
      setIsModalOpen(false);
      setEditingCoach(null);
      setFormData({ ...initialForm, merchant_id: merchants[0]?.id || "" });
      fetchCoaches();
    } catch (error) {
      console.error("Failed to save cardio coach:", error);
      alert(error.response?.data?.detail || "Gagal menyimpan nama coach cardio.");
    }
  };

  return (
    <DashboardLayout>
      <Header title="Cardio Coach Menu" />
      <div className="space-y-8 p-8" data-testid="cardio-coach-menu-page">
        <section className="rounded-sm border border-white/10 bg-zinc-900/50 p-6">
          <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <h2 className="text-xl font-semibold text-white">Daftar Nama Coach Cardio</h2>
              <p className="mt-1 text-sm text-zinc-500">
                {isAdmin
                  ? "Klik nama coach atau tombol Riwayat untuk melihat kelas yang pernah dijalankan."
                  : "Mode kasir: bisa menambahkan jadwal kelas dan mengubah status Done/Not Done/Cancel."}
              </p>
            </div>
            {isAdmin && (
              <Button
                type="button"
                onClick={openCreate}
                className="rounded-sm bg-[#D4AF37] font-semibold text-black hover:bg-[#C59A27]"
              >
                <Plus className="mr-2 h-4 w-4" strokeWidth={2} />
                Tambah Nama Coach
              </Button>
            )}
          </div>

          <div className="overflow-hidden rounded-sm border border-white/10 bg-black/20">
            <Table>
              <TableHeader>
                <TableRow className="border-white/10 hover:bg-transparent">
                  <TableHead>No</TableHead>
                  <TableHead>Nama Coach Cardio</TableHead>
                  <TableHead>Merchant</TableHead>
                  <TableHead>No HP</TableHead>
                  <TableHead>Rekening</TableHead>
                  <TableHead>Foto KTP</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Aksi</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={8} className="py-8 text-center">
                      <div className="mx-auto h-6 w-6 animate-spin rounded-full border-2 border-[#D4AF37] border-t-transparent" />
                    </TableCell>
                  </TableRow>
                ) : sortedCoaches.length > 0 ? (
                  sortedCoaches.map((item, index) => (
                    <TableRow key={item.id || index} className="border-white/5 hover:bg-zinc-900/50 transition-colors">
                      <TableCell className="py-4 text-sm text-zinc-300">{index + 1}</TableCell>
                      <TableCell className="py-4 text-sm font-medium text-white">
                        <button
                          type="button"
                          onClick={() => openCoachMembers(item)}
                          className="rounded-sm text-left text-[#F3D77A] underline-offset-4 transition hover:text-[#D4AF37] hover:underline"
                        >
                          {item.name}
                        </button>
                      </TableCell>
                      <TableCell className="py-4 text-sm text-zinc-300">{item.merchant_name || "-"}</TableCell>
                      <TableCell className="py-4 text-sm text-zinc-300">{item.phone || "-"}</TableCell>
                      <TableCell className="py-4 text-sm text-zinc-300">
                        {item.bank_name || item.bank_account_name || item.bank_account_number ? (
                          <div className="space-y-1">
                            <p className="font-semibold text-zinc-100">
                              {item.bank_account_name || item.bank_account_number || "-"}
                            </p>
                            <p className="text-xs text-zinc-500">
                              {item.bank_name || "-"}
                              {item.bank_account_name && item.bank_account_number ? ` - ${item.bank_account_number}` : ""}
                            </p>
                          </div>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                      <TableCell className="py-4 text-sm">
                        {item.ktp_photo_path ? (
                          <a
                            href={resolveFileUrl(item.ktp_photo_path)}
                            target="_blank"
                            rel="noreferrer"
                            className="font-medium text-sky-300 hover:text-sky-200"
                          >
                            Lihat KTP
                          </a>
                        ) : (
                          <span className="text-zinc-500">-</span>
                        )}
                      </TableCell>
                      <TableCell className="py-4">
                        <Badge
                          variant="outline"
                          className={`rounded-sm text-xs ${
                            item.is_active !== false
                              ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-400"
                              : "border-zinc-500/30 bg-zinc-500/10 text-zinc-400"
                          }`}
                        >
                          {item.is_active !== false ? "Active" : "Inactive"}
                        </Badge>
                      </TableCell>
                      <TableCell className="py-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => openCoachMembers(item, { historyMode: true })}
                            className="inline-flex items-center gap-1 rounded-sm border border-[#D4AF37]/30 bg-[#D4AF37]/10 px-2 py-1 text-[11px] font-semibold text-[#F3D77A] transition-colors hover:bg-[#D4AF37]/15"
                            title="Lihat riwayat kelas"
                          >
                            <History className="h-3.5 w-3.5" strokeWidth={1.7} />
                            Riwayat
                          </button>
                          {isAdmin ? (
                            <button
                              type="button"
                              onClick={() => openEdit(item)}
                              className="rounded-sm p-2 text-zinc-400 transition-colors hover:bg-zinc-800 hover:text-white"
                            >
                              <Edit2 className="h-4 w-4" strokeWidth={1.5} />
                            </button>
                          ) : (
                            <span className="text-xs text-zinc-500">View only</span>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={8} className="py-8 text-center text-sm text-zinc-500">
                      Belum ada nama coach cardio.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </section>

        <Dialog
          open={isModalOpen}
          onOpenChange={(open) => {
            setIsModalOpen(open);
            if (!open) setEditingCoach(null);
          }}
        >
          <DialogContent className={`max-w-2xl ${modalDialogClass}`}>
            <DialogHeader>
              <DialogTitle className="text-lg font-semibold">
                {editingCoach ? "Edit Nama Coach Cardio" : "Tambah Nama Coach Cardio"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="mt-4 space-y-4">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Merchant</Label>
                  <Select
                    value={formData.merchant_id || undefined}
                    onValueChange={(value) => setFormData((prev) => ({ ...prev, merchant_id: value }))}
                    disabled={!isAdmin}
                  >
                    <SelectTrigger className={modalFieldClass}>
                      <SelectValue placeholder="Pilih merchant" />
                    </SelectTrigger>
                    <SelectContent className="border-white/10 bg-zinc-950 text-white">
                      {merchants.map((merchant) => (
                        <SelectItem key={merchant.id} value={merchant.id}>
                          {merchant.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {isAdmin && merchants.length === 0 ? (
                    <p className="text-xs text-amber-400">Tambahkan Merchant dulu sebelum membuat Master Coach.</p>
                  ) : null}
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Nama Coach</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                    className={modalFieldClass}
                    placeholder="Contoh: Coach Dika"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">No HP</Label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData((prev) => ({ ...prev, phone: e.target.value }))}
                    className={modalFieldClass}
                    placeholder="Contoh: 08123456789"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Tipe Bank</Label>
                  <Select
                    value={formData.bank_name || undefined}
                    onValueChange={(value) => setFormData((prev) => ({ ...prev, bank_name: value }))}
                    disabled
                  >
                    <SelectTrigger className={modalFieldClass}>
                      <SelectValue placeholder="Pilih bank" />
                    </SelectTrigger>
                    <SelectContent className="border-white/10 bg-zinc-950 text-white">
                      {[...new Set([formData.bank_name, ...bankOptions].filter(Boolean))].map((bank) => (
                        <SelectItem key={bank} value={bank}>{bank}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Account Name</Label>
                  <Input
                    value={formData.bank_account_name}
                    onChange={(e) => setFormData((prev) => ({ ...prev, bank_account_name: e.target.value }))}
                    className={modalFieldClass}
                    placeholder="Nama sesuai rekening"
                    disabled
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">No Rekening</Label>
                  <Input
                    value={formData.bank_account_number}
                    onChange={(e) => setFormData((prev) => ({ ...prev, bank_account_number: e.target.value }))}
                    className={modalFieldClass}
                    placeholder="Nomor rekening coach"
                    disabled
                  />
                </div>
              </div>
              <p className="rounded-sm border border-[#D4AF37]/20 bg-[#D4AF37]/5 px-3 py-2 text-xs text-[#F3D77A]">
                Data rekening hanya bisa diubah dari menu Master Rekening.
              </p>
              <div className="space-y-2 rounded-sm border border-white/10 bg-black/20 p-3">
                <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Foto KTP</Label>
                <div className="flex flex-col gap-3 md:flex-row md:items-center">
                  <Input
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    onChange={handleKtpUpload}
                    className={modalFieldClass}
                    disabled={uploadingKtp}
                  />
                  {formData.ktp_photo_path ? (
                    <a
                      href={resolveFileUrl(formData.ktp_photo_path)}
                      target="_blank"
                      rel="noreferrer"
                      className="whitespace-nowrap text-sm font-medium text-sky-300 hover:text-sky-200"
                    >
                      Lihat Foto KTP
                    </a>
                  ) : null}
                </div>
                {uploadingKtp ? <p className="text-xs text-zinc-500">Mengupload foto KTP...</p> : null}
                {formData.ktp_photo_path ? (
                  <button
                    type="button"
                    onClick={() => setFormData((prev) => ({ ...prev, ktp_photo_path: "" }))}
                    className="text-xs text-rose-300 hover:text-rose-200"
                  >
                    Hapus lampiran KTP
                  </button>
                ) : null}
              </div>
              <div className="grid grid-cols-1 items-center gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Sort Order</Label>
                  <Input
                    type="number"
                    value={formData.sort_order}
                    onChange={(e) => setFormData((prev) => ({ ...prev, sort_order: e.target.value }))}
                    className={modalFieldClass}
                  />
                </div>
                <div className="flex items-center justify-between pt-5 md:justify-start md:gap-3">
                  <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Active</Label>
                  <Switch
                    checked={formData.is_active}
                    onCheckedChange={(checked) => setFormData((prev) => ({ ...prev, is_active: checked }))}
                  />
                </div>
              </div>
              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsModalOpen(false)}
                  className={modalOutlineButtonClass}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={!isAdmin || merchants.length === 0}
                  className="flex-1 rounded-sm bg-[#D4AF37] font-semibold text-black hover:bg-[#C59A27]"
                >
                  {editingCoach ? "Update Nama Coach" : "Tambah Nama Coach"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        <Dialog
          open={isCoachMembersOpen}
          onOpenChange={(open) => {
            setIsCoachMembersOpen(open);
            if (!open) {
              setSelectedCoach(null);
              setIsCoachHistoryMode(false);
              setCoachMembers([]);
              setSelectedClassKey("");
              setCoachMonthFilter("");
              setManualClassOptions([]);
              setManualClassForm({
                class_name: "",
                class_date: todayDateValue(),
              });
            }
          }}
        >
          <DialogContent className="max-h-[92vh] w-[min(1180px,calc(100vw-32px))] max-w-none overflow-y-auto border-transparent bg-zinc-900 p-5 text-white shadow-2xl ring-1 ring-white/[0.04]">
            <DialogHeader>
              <DialogTitle className="text-lg font-semibold">
                {isCoachHistoryMode ? "Riwayat Kelas" : "Member Kelas"} - Coach {selectedCoach?.name || "-"}
              </DialogTitle>
            </DialogHeader>

            {coachMembersLoading ? (
              <div className="py-10 text-center">
                <div className="mx-auto h-6 w-6 animate-spin rounded-full border-2 border-[#D4AF37] border-t-transparent" />
              </div>
            ) : (
              <div className="space-y-4 pr-1">
                {!isCoachHistoryMode && (
                <form onSubmit={handleAddManualClass} className="space-y-3 rounded-sm border border-white/10 bg-black/20 p-3">
                  <h3 className="text-sm font-semibold text-white">Tambah Jadwal Kelas</h3>
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                    <div className="space-y-1">
                      <Label className="text-[11px] uppercase tracking-[0.14em] text-zinc-500">Nama Kelas</Label>
                      <Select
                        value={manualClassForm.class_name || undefined}
                        onValueChange={(value) =>
                          setManualClassForm((prev) => ({ ...prev, class_name: String(value || "") }))
                        }
                        disabled={manualClassOptions.length === 0}
                      >
                        <SelectTrigger className={modalFieldClass}>
                          <SelectValue placeholder="Pilih kelas" />
                        </SelectTrigger>
                        <SelectContent>
                          {manualClassOptions.map((className) => (
                            <SelectItem key={className} value={className}>{className}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {manualClassOptions.length === 0 && (
                        <p className="text-[11px] text-amber-400">
                          Belum ada kelas cardio aktif di menu Plans untuk merchant coach ini.
                        </p>
                      )}
                    </div>
                    <div className="space-y-1">
                      <Label className="text-[11px] uppercase tracking-[0.14em] text-zinc-500">Tanggal Kelas</Label>
                      <Input
                        type="date"
                        value={manualClassForm.class_date}
                        onChange={(e) => setManualClassForm((prev) => ({ ...prev, class_date: e.target.value }))}
                        className={modalFieldClass}
                        required
                      />
                    </div>
                  </div>
                  <div className="flex justify-end">
                    <Button
                      type="submit"
                      disabled={addingManualClass || !manualClassForm.class_name}
                      className="rounded-sm bg-[#D4AF37] font-semibold text-black hover:bg-[#C59A27]"
                    >
                      {addingManualClass ? "Menyimpan..." : "Tambah Kelas"}
                    </Button>
                  </div>
                </form>
                )}

                <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
                  <div className="rounded-sm border border-white/10 bg-black/20 p-3">
                    <p className="text-[11px] uppercase tracking-[0.14em] text-zinc-500">Total Kelas</p>
                    <p className="mt-1 text-lg font-semibold text-white">{visibleCoachSummary.totalClasses}</p>
                  </div>
                  <div className="rounded-sm border border-white/10 bg-black/20 p-3">
                    <p className="text-[11px] uppercase tracking-[0.14em] text-zinc-500">Total Member</p>
                    <p className="mt-1 text-lg font-semibold text-white">{visibleCoachSummary.totalMembers}</p>
                  </div>
                  <div className="rounded-sm border border-emerald-500/20 bg-emerald-500/10 p-3">
                    <p className="text-[11px] uppercase tracking-[0.14em] text-emerald-300">Done</p>
                    <p className="mt-1 text-lg font-semibold text-emerald-300">{visibleCoachSummary.doneCount}</p>
                  </div>
                  <div className="rounded-sm border border-rose-500/20 bg-rose-500/10 p-3">
                    <p className="text-[11px] uppercase tracking-[0.14em] text-rose-300">Not Done</p>
                    <p className="mt-1 text-lg font-semibold text-rose-300">{visibleCoachSummary.notDoneCount}</p>
                  </div>
                  <div className="rounded-sm border border-amber-500/20 bg-amber-500/10 p-3">
                    <p className="text-[11px] uppercase tracking-[0.14em] text-amber-300">Cancel</p>
                    <p className="mt-1 text-lg font-semibold text-amber-300">{visibleCoachSummary.cancelledCount}</p>
                  </div>
                </div>

                <div className="overflow-x-auto rounded-sm border border-white/10 bg-black/20">
                  <div className="flex flex-col gap-3 border-b border-white/10 px-3 py-3 md:flex-row md:items-end md:justify-between">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-[0.1em] text-zinc-500">
                        {isCoachHistoryMode ? "Riwayat Kelas Coach" : "Detail Kelas Coach"}
                      </p>
                      <p className="mt-1 text-[11px] text-zinc-600">Filter berdasarkan bulan tanggal kelas.</p>
                    </div>
                    <div className="grid gap-2 md:grid-cols-[180px_auto]">
                      <div className="space-y-1">
                        <Label className="text-[10px] uppercase tracking-[0.14em] text-zinc-500">Bulan Kelas</Label>
                        <Select
                          value={coachMonthFilter || "all"}
                          onValueChange={(value) => setCoachMonthFilter(value === "all" ? "" : value)}
                        >
                          <SelectTrigger className={modalFieldClass}>
                            <SelectValue placeholder="Pilih bulan" />
                          </SelectTrigger>
                          <SelectContent className="border-white/10 bg-zinc-950 text-white">
                            <SelectItem value="all">Semua Bulan</SelectItem>
                            {monthOptions.map((month) => (
                              <SelectItem key={month.value} value={month.value}>
                                {month.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setCoachMonthFilter("")}
                        className="self-end rounded-sm border-white/10 text-white hover:bg-zinc-800"
                      >
                        Reset
                      </Button>
                    </div>
                  </div>
                  <Table className="min-w-[1080px]">
                    <TableHeader>
                      <TableRow className="border-white/10 hover:bg-transparent">
                        <TableHead>No</TableHead>
                        <TableHead>Kelas</TableHead>
                        <TableHead>Tanggal Kelas</TableHead>
                        <TableHead>Total Sesi</TableHead>
                        <TableHead>Done</TableHead>
                        <TableHead>Not Done</TableHead>
                        <TableHead>Cancel</TableHead>
                        <TableHead>Update Terakhir</TableHead>
                        <TableHead className="text-right">Aksi</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {visibleCoachClassGroups.length > 0 ? (
                        visibleCoachClassGroups.map((item, index) => (
                          <TableRow key={item.class_key || index} className="border-white/5 hover:bg-zinc-900/50 transition-colors">
                            <TableCell className="py-3 text-sm text-zinc-300">{index + 1}</TableCell>
                            <TableCell className="py-3 text-sm font-semibold text-white">{item.class_name}</TableCell>
                            <TableCell className="py-3 text-sm font-medium text-white">
                              {formatDateOnly(item.class_date)}
                            </TableCell>
                            <TableCell className="py-3 text-sm text-zinc-300">{item.totalSessions}</TableCell>
                            <TableCell className="py-3 text-sm text-emerald-300">{item.classDoneCount}</TableCell>
                            <TableCell className="py-3 text-sm text-rose-300">{item.classNotDoneCount}</TableCell>
                            <TableCell className="py-3 text-sm text-amber-300">{item.classCancelledCount}</TableCell>
                            <TableCell className="py-3 text-sm text-zinc-400">
                              <span className="text-zinc-300">{item.latestSession?.day_of_week || "-"}</span>
                              <br />
                              <span className="text-[11px] text-zinc-500">{formatDateOnly(item.class_date)}</span>
                            </TableCell>
                            <TableCell className="py-3 text-right">
                              <div className="flex justify-end gap-2">
                                <Button
                                  type="button"
                                  variant="outline"
                                  onClick={() => setSelectedClassKey(item.class_key)}
                                  className="rounded-sm border-white/20 bg-white/[0.02] text-zinc-200 hover:bg-white/[0.06]"
                                >
                                  Detail Sesi
                                </Button>
                                {!isCoachHistoryMode && (
                                <button
                                  type="button"
                                  onClick={() => requestClassDoneStatus(item, "done")}
                                  disabled={!!item.classStatus}
                                  className={`rounded-sm border px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.08em] ${
                                    item.classStatus === "done"
                                      ? "cursor-not-allowed border-emerald-500/40 bg-emerald-500/20 text-emerald-200 opacity-70"
                                      : item.classStatus
                                      ? "cursor-not-allowed border-white/10 bg-white/[0.01] text-zinc-600"
                                      : "border-white/15 bg-white/[0.02] text-zinc-300 hover:bg-emerald-500/10"
                                  }`}
                                >
                                  Done
                                </button>
                                )}
                                {!isCoachHistoryMode && (
                                <button
                                  type="button"
                                  onClick={() => requestClassDoneStatus(item, "not_done")}
                                  disabled={!!item.classStatus}
                                  className={`rounded-sm border px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.08em] ${
                                    item.classStatus === "not_done"
                                      ? "cursor-not-allowed border-rose-500/40 bg-rose-500/20 text-rose-200 opacity-70"
                                      : item.classStatus
                                      ? "cursor-not-allowed border-white/10 bg-white/[0.01] text-zinc-600"
                                      : "border-white/15 bg-white/[0.02] text-zinc-300 hover:bg-rose-500/10"
                                  }`}
                                >
                                  Not Done
                                </button>
                                )}
                                {!isCoachHistoryMode && (
                                <button
                                  type="button"
                                  onClick={() => requestClassDoneStatus(item, "cancelled")}
                                  disabled={!!item.classStatus}
                                  className={`rounded-sm border px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.08em] ${
                                    item.classStatus === "cancelled"
                                      ? "cursor-not-allowed border-amber-500/40 bg-amber-500/20 text-amber-200 opacity-70"
                                      : item.classStatus
                                      ? "cursor-not-allowed border-white/10 bg-white/[0.01] text-zinc-600"
                                      : "border-white/15 bg-white/[0.02] text-zinc-300 hover:bg-amber-500/10"
                                  }`}
                                  title="Cancel kelas karena coach izin"
                                >
                                  Cancel
                                </button>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={8} className="py-8 text-center text-sm text-zinc-500">
                            <div className="flex flex-col items-center gap-2">
                              <UsersRound className="h-5 w-5 text-zinc-600" />
                              {isCoachHistoryMode
                                ? "Belum ada riwayat kelas yang sudah done untuk coach ini."
                                : "Belum ada member yang ikut kelas coach ini."}
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>

                {selectedClassSessions ? (
                  <div className="space-y-3 rounded-sm border border-white/10 bg-black/20 p-3">
                    <div className="flex items-center justify-between">
                      <h3 className="text-sm font-semibold text-white">
                        Detail Sesi - {selectedClassSessions.class_name} ({formatDateOnly(selectedClassSessions.class_date)})
                      </h3>
                      <button
                        type="button"
                        onClick={() => setSelectedClassKey("")}
                        className="rounded-sm border border-white/15 px-2 py-1 text-[11px] text-zinc-300 hover:bg-white/[0.06]"
                      >
                        Tutup Detail
                      </button>
                    </div>

                    <div className="overflow-hidden rounded-sm border border-white/10">
                      <Table>
                        <TableHeader>
                          <TableRow className="border-white/10 hover:bg-transparent">
                            <TableHead>No</TableHead>
                            <TableHead>Member</TableHead>
                            <TableHead>Jadwal</TableHead>
                            <TableHead className="text-right">Aksi</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {(selectedClassSessions.displaySessions || []).map((session, index) => (
                            <TableRow key={`${session.source_type}-${session.id}-${index}`} className="border-white/5">
                              <TableCell className="py-3 text-sm text-zinc-300">{index + 1}</TableCell>
                              <TableCell className="py-3 text-sm text-zinc-200">{session.full_name}</TableCell>
                              <TableCell className="py-3 text-sm text-zinc-400">
                                {session.day_of_week}
                                <br />
                                <span className="text-[11px] text-zinc-500">{formatDateOnly(session.class_date || session.created_at)}</span>
                              </TableCell>
                              <TableCell className="py-3 text-right">
                                {!isCoachHistoryMode && isAdmin && (session.source_type === "booking" || session.source_type === "payment") ? (
                                  <button
                                    type="button"
                                    onClick={() => deleteClassSession(session)}
                                    className="rounded-sm p-2 text-zinc-400 transition-colors hover:bg-red-500/10 hover:text-red-400"
                                    title="Hapus kelas"
                                  >
                                    <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                                  </button>
                                ) : (
                                  <span className="text-xs text-zinc-500">-</span>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                          {(selectedClassSessions.displaySessions || []).length === 0 ? (
                            <TableRow className="border-white/5">
                              <TableCell colSpan={4} className="py-6 text-center text-sm text-zinc-500">
                                Belum ada member yang daftar pada jadwal kelas ini.
                              </TableCell>
                            </TableRow>
                          ) : null}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                ) : null}
              </div>
            )}
          </DialogContent>
        </Dialog>
        <AlertDialog open={!!pendingStatusUpdate} onOpenChange={(open) => !open && setPendingStatusUpdate(null)}>
          <AlertDialogContent className="border-white/10 bg-zinc-900 text-white">
            <AlertDialogHeader>
              <AlertDialogTitle>Konfirmasi Status Kelas</AlertDialogTitle>
              <AlertDialogDescription className="text-zinc-400">
                Apakah Anda yakin ingin mengubah status kelas{" "}
                <span className="font-semibold text-white">
                  {pendingStatusUpdate?.group?.class_name || "-"}
                </span>{" "}
                menjadi{" "}
                <span className={getClassStatusTextClass(pendingStatusUpdate?.nextStatus)}>
                  {getClassStatusLabel(pendingStatusUpdate?.nextStatus)}
                </span>
                ?
              </AlertDialogDescription>
            </AlertDialogHeader>
            {pendingStatusUpdate?.group?.isEmptyClass ? (
              <div className="rounded-sm border border-amber-500/25 bg-amber-500/10 p-3 text-xs leading-relaxed text-amber-200">
                Jadwal kelas ini belum memiliki member. Status default tetap kosong sampai Anda memilih Done, Not Done, atau Cancel.
              </div>
            ) : null}
            <AlertDialogFooter>
              <AlertDialogCancel className="rounded-sm border-white/10 bg-white/[0.03] text-white hover:bg-white/[0.06]">
                Tidak
              </AlertDialogCancel>
              <AlertDialogAction
                className="rounded-sm bg-[#D4AF37] font-semibold text-black hover:bg-[#C59A27]"
                onClick={async () => {
                  const pending = pendingStatusUpdate;
                  setPendingStatusUpdate(null);
                  if (pending?.group && pending?.nextStatus) {
                    await updateClassDoneStatus(pending.group, pending.nextStatus);
                  }
                }}
              >
                Iya
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardLayout>
  );
}
